import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";

import ModulePage from "./pages/ModulePage";
import MyAttendance from "./pages/MyAttendance";
import Directory from "./pages/Directory";
import MyAssets from "./pages/MyAssets";
import Resignation from "./pages/Resignation";
import Tasks from "./pages/Tasks";
import MySales from "./pages/MySales";
import MyOffboarding from "./pages/MyOffboarding";
import MyOnboarding from "./pages/MyOnboarding";
import BackupRestore from "./pages/BackupRestore";
import AdminHub from "./pages/AdminHub";
import MasterAccess from "./pages/MasterAccess";
import { initTheme } from "./components/ThemeToggle";
import { Toaster } from "sonner";

// Apply the saved theme before the first paint to avoid a white flash for dark-mode users.
initTheme();

// v109.9.159 — Suppress the benign "ResizeObserver loop completed with
// undelivered notifications" warning that shadcn/Radix components trigger
// when their content resizes during layout. It doesn't affect behavior
// but produces the red webpack-dev overlay. Ref:
//   https://github.com/facebook/create-react-app/issues/11562
if (typeof window !== "undefined") {
  const _origError = window.onerror;
  window.addEventListener("error", (e) => {
    if (e.message && /ResizeObserver loop/.test(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return true;
    }
  }, true);
  window.addEventListener("unhandledrejection", (e) => {
    if (e.reason && /ResizeObserver loop/.test(String(e.reason?.message || e.reason))) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });
  void _origError;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout><Dashboard /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute masterOnly>
                <Layout><Employees /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-attendance"
            element={
              <ProtectedRoute>
                <Layout><MyAttendance /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/resignation"
            element={
              <ProtectedRoute>
                <Layout><Resignation /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/directory"
            element={
              <ProtectedRoute>
                <Layout><Directory /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-assets"
            element={
              <ProtectedRoute>
                <Layout><MyAssets /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <Layout><Tasks /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave-management"
            element={<Navigate to="/m/hr_leaves" replace />}
          />
          <Route
            path="/salary-hike"
            element={<Navigate to="/m/hr?tab=salary_hike" replace />}
          />
          <Route
            path="/my-sales"
            element={
              <ProtectedRoute>
                <Layout><MySales /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-admin"
            element={<Navigate to="/my-sales?tab=sales_admin" replace />}
          />
          <Route
            path="/my-offboarding"
            element={
              <ProtectedRoute>
                <MyOffboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-onboarding"
            element={
              <ProtectedRoute>
                <MyOnboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-hub"
            element={
              <ProtectedRoute masterOnly>
                <Layout><AdminHub /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/master-access"
            element={
              <ProtectedRoute masterOnly>
                <Layout><MasterAccess /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/backup-restore"
            element={
              <ProtectedRoute masterOnly>
                <Layout><BackupRestore /></Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/contact-delete-requests"
            element={<Navigate to="/m/crm_reports?tab=contact-approvals" replace />}
          />
          <Route
            path="/m/:moduleKey"
            element={
              <ProtectedRoute>
                <Layout><ModulePage /></Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
